import type React from 'react';
import { type FormEvent, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as reactQrCode from 'react-qr-code';

// O IMPORT E DEFENSIVO PORQUE O PACOTE E CommonJS E A INTEROPERACAO MUDOU.
//
// "react-qr-code" 2.0.15 nao declara "module", "exports" nem "type" no package.json: e
// CommonJS puro, com "exports.default" e "exports.QRCode" no fim do arquivo. Com o
// bundler novo da Vite 8 (Rolldown), o "import QRCode from" resolvia para um OBJETO que
// o React 19 recusa como tipo de elemento — erro #130 com args[]=object, e tela branca
// em producao no clique de "Gerar segredo".
//
// O TESTE NAO PEGAVA, E ESSA E A PARTE QUE IMPORTA. O vitest resolve modulo pelo proprio
// pipeline, e nao pelo Rolldown: o mesmo componente renderiza VERDE em jsdom e quebra no
// bundle publicado. Nenhum teste desta suite alcanca essa diferenca, e escrever um que
// alcance exigiria exercitar o artefato de producao num navegador — que e a lacuna que a
// suite tem, e nao um teste que faltou escrever aqui.
//
// POR ISSO A DEFESA REAL NAO E ESTE IMPORT, e sim o ErrorBoundary la embaixo: ele nao
// depende de eu ter acertado a forma do modulo. Se o QR quebrar de novo, por este motivo
// ou por outro, a pessoa continua vendo o segredo em texto — que e o que ela precisa
// copiar para o aplicativo autenticador.
const QRCode = ((reactQrCode as unknown as { default?: unknown }).default ??
  (reactQrCode as unknown as { QRCode?: unknown }).QRCode ??
  reactQrCode) as React.ComponentType<{ value: string; size?: number }>;

import { isProblemError } from '../../api/client';
import { getGetMeQueryKey, useEnrollMfa, useVerifyMfa } from '../../api/generated/identity/identity';
import { LanguageSelector } from '../../app/LanguageSelector';
import { Alert } from '../../design/primitives/Alert';
import { ErrorBoundary } from '../../design/primitives/ErrorBoundary';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { useLogoutAction } from './useSession';
import styles from './auth.module.css';

export function MfaEnrollmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const enroll = useEnrollMfa();
  const verify = useVerifyMfa();
  const logout = useLogoutAction();
  const [code, setCode] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);

  // Mesma guarda do desafio: `noValidate` deixa `required` e `pattern` como
  // decoracao, e codigo incompleto vira 401 anunciado como "codigo incorreto".
  const canSubmit = /^[0-9]{6}$/.test(code) && !verify.isPending;

  const startEnrollment = (): void => {
    enroll.mutate(undefined, {
      onSuccess: (result) => {
        setSecret(result.secret);
        setOtpauthUri(result.otpauthUri);
      },
    });
  };

  const handleVerify = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSubmit) return;
    verify.mutate(
      { data: { code } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          void navigate('/', { replace: true });
        },
      },
    );
  };

  const errorMessage = ((): string | undefined => {
    const err = verify.error ?? enroll.error;
    if (!err) return undefined;
    if (!isProblemError(err)) return t('error.unexpected');
    if (err.status === 409) return t('mfa.enrollAlreadyDone');
    if (err.status === 401 || err.status === 400) return t('mfa.invalidCode');
    return t('error.unexpected');
  })();

  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('mfa.enrollTitle')}</h1>
        <p className={styles.subtitle}>{t('mfa.enrollWhy')}</p>
        <p className={styles.subtitle}>{t('mfa.enrollSecretOnce')}</p>

        {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}

        {!secret ? (
          <Button variant="primary" loading={enroll.isPending} onClick={startEnrollment}>
            {enroll.isPending ? t('mfa.enrollStarting') : t('mfa.enrollStart')}
          </Button>
        ) : (
          <>
            {otpauthUri ? (
              <ErrorBoundary
                area="mfa-qr"
                fallback={<Alert tone="info">{t('mfa.enrollQrUnavailable')}</Alert>}
              >
                <div className={styles.qrWrap} aria-label={t('mfa.enrollQrAlt')}>
                  <QRCode value={otpauthUri} size={180} />
                </div>
              </ErrorBoundary>
            ) : null}
            <p>
              <strong>{t('mfa.enrollSecret')}:</strong>{' '}
              <code className={styles.secret}>{secret}</code>
            </p>
            <form onSubmit={handleVerify} noValidate>
              <Field
                label={t('mfa.code')}
                hint={t('mfa.codeHint')}
                name="code"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{6}"
              />
              <Button
                className={styles.submit}
                type="submit"
                variant="primary"
                loading={verify.isPending}
                disabled={!canSubmit}
              >
                {verify.isPending ? t('mfa.submitting') : t('mfa.submit')}
              </Button>
            </form>
          </>
        )}

        {/*
          Sair tambem daqui: quem fecha a pagina depois de gerar o segredo e
          antes de verificar volta a esta tela sem caminho visivel de recomeco,
          e a inscricao so pode ser refeita fora da plataforma. O botao existe
          para que a sessao pendente nao fique aberta na maquina.
        */}
        <p className={styles.subtitle}>{t('mfa.lockedOut')}</p>
        <Button onClick={() => logout.mutate()} loading={logout.isPending}>
          {t('nav.logout')}
        </Button>
        {logout.isError ? <Alert tone="error">{t('nav.logoutFailed')}</Alert> : null}

        <div className={styles.footer}>
          <LanguageSelector />
        </div>
      </div>
    </main>
  );
}
